import React from 'react';
import { HelpTooltip } from './HelpTooltip';


interface AspectRatioOption {
  value: string;
  label: string;
  description: string;
}

interface AspectRatioWidgetProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  description?: string;
  helpText?: string;
}

// Aspect ratio presets matching CCStudio_AspectRatioSelector node
const ASPECT_RATIO_PRESETS: AspectRatioOption[] = [
  { value: '1:1', label: '1:1', description: 'Square' },
  { value: '2:3', label: '2:3', description: 'Portrait' },
  { value: '3:2', label: '3:2', description: 'Landscape' },
  { value: '3:4', label: '3:4', description: 'Portrait' },
  { value: '4:3', label: '4:3', description: 'Landscape' },
  { value: '4:5', label: '4:5', description: 'Portrait' },
  { value: '5:4', label: '5:4', description: 'Landscape' },
  { value: '9:16', label: '9:16', description: 'Vertical/Mobile' },
  { value: '16:9', label: '16:9', description: 'Widescreen' },
  { value: '21:9', label: '21:9', description: 'Ultra-wide' },
];

export const AspectRatioWidget: React.FC<AspectRatioWidgetProps> = ({
  label,
  value,
  onChange,
  error,
  disabled = false,
  required = false,
  description,
  helpText
}) => {
  // Ensure we have a valid value
  const safeValue = value || '1:1';
  
  // Find the current preset
  const currentPreset = ASPECT_RATIO_PRESETS.find(preset => preset.value === safeValue);

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange(e.target.value);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <label className="text-label font-medium text-gray-700">
          {label}{required && <span className="text-error ml-0.5">*</span>}
        </label>
        {helpText && <HelpTooltip helpText={helpText} />}
      </div>
      {description && <p className="text-label text-gray-500">{description}</p>}
      <select aria-label={label} value={safeValue} onChange={handleChange} disabled={disabled}
        className={`w-full px-3 py-2.5 border rounded-md text-body bg-white outline-none focus:border-blue-400 focus:shadow-focus transition-[border-color,box-shadow] duration-fast select-input disabled:bg-gray-100 ${error ? 'border-error' : 'border-gray-300'}`}>
        {ASPECT_RATIO_PRESETS.map(preset => (
          <option key={preset.value} value={preset.value}>{preset.label} ({preset.description})</option>
        ))}
      </select>
      {error && <p className="text-label text-error">{error}</p>}
      {currentPreset && <p className="text-meta text-gray-400">Selected: {currentPreset.label} — {currentPreset.description}</p>}
    </div>
  );
};

export default AspectRatioWidget;

